/**
 * Off-topic guard for Tennis-Matches chat: score-based heuristics only, no LLM.
 * Keywords and offTopicSignals loaded from scopePolicy.config.json with fallback to built-in defaults.
 * No country/entity lists; generic signal patterns only.
 */

import scopePolicyConfig from "./scopePolicy.config.json";

export type ScopeResult = {
  allowed: boolean;
  reason?: "off_topic";
};

export interface ScopePolicyConfig {
  keywords?: string[];
  /** Generic off-topic signal patterns (regex source strings). No country/entity lists. */
  offTopicSignals?: string[];
  /** @deprecated Use offTopicSignals. Kept for fallback. */
  offTopicPatterns?: string[];
}

const DEFAULT_KEYWORDS: string[] = [
  "tennis",
  "turnier",
  "spieler",
  "schleifchen",
  "clubmeisterschaft",
  "match",
  "ergebnis",
  "ranking",
  "lk",
  "medenspiel",
  "training",
  "aufschlag",
  "return",
  "tiebreak",
  "satz",
  "platz",
  "verein",
  "trainerstunden",
  "rangliste",
  "wettkampf",
  "meisterschaft",
  "spielplan",
  "paarung",
  "runde",
  "gruppenphase",
  "ko",
  "doppel",
  "einzel",
];

const DEFAULT_OFF_TOPIC_SIGNALS: string[] = [
  "\\b(wetter|weather)\\b",
  "\\b(übersetze|translate)\\b",
  "\\b(übersetze|translate)\\s+\\w+",
  "\\b(rechne|calculate)\\b",
  "\\b(hauptstadt|capital\\s+of)\\b",
  "\\b(hauptstadt\\s+von)\\b",
  "\\b(wer\\s+ist|who\\s+is)\\b",
  "\\b(wer\\s+ist|who\\s+is)\\s+(der|die|das|the)?\\s*\\w+",
  "\\b(präsident|president|kanzler|minister)\\b",
  "\\b\\d+\\s*[*×x/+\\-]\\s*\\d+\\b",
  "\\b\\d+\\s*mal\\s*\\d+\\b",
  "\\bwie\\s+alt\\s+ist\\b",
  "\\b(geschichte|history\\s+of)\\b",
  "\\b(wie\\s+ist\\s+das)\\b",
];

function stringsToRegExps(strings: string[]): RegExp[] {
  return strings.map((s) => new RegExp(s, "i"));
}

const config = scopePolicyConfig as ScopePolicyConfig;
const TENNIS_KEYWORDS: readonly string[] =
  config?.keywords && config.keywords.length > 0 ? config.keywords : DEFAULT_KEYWORDS;

const signalStrings =
  config?.offTopicSignals && config.offTopicSignals.length > 0
    ? config.offTopicSignals
    : config?.offTopicPatterns && config.offTopicPatterns.length > 0
      ? config.offTopicPatterns
      : DEFAULT_OFF_TOPIC_SIGNALS;

const OFF_TOPIC_SIGNALS: RegExp[] = stringsToRegExps(signalStrings);

/** Escape special regex chars in a literal string for use inside RegExp. */
function escapeRegexLiteral(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Tennis keywords compiled to regexes once. Word boundaries avoid false positives (e.g. "ko" in "kochen"). */
const TENNIS_REGEXES: RegExp[] = (TENNIS_KEYWORDS as string[]).map((kw) => {
  const escaped = escapeRegexLiteral(kw);
  return new RegExp(`\\b${escaped}\\b`, "i");
});

function getTennisScore(text: string): number {
  return TENNIS_REGEXES.some((re) => re.test(text)) ? 1 : 0;
}

function getOffTopicScore(text: string): number {
  return OFF_TOPIC_SIGNALS.filter((re) => re.test(text)).length;
}

/**
 * Evaluates whether the message is in scope for Tennis-Matches (no LLM).
 * Score-based: tennisScore from keywords; offTopicScore from generic signal patterns.
 * - If tennisScore >= 1 => allowed.
 * - Else if offTopicScore >= 1 and tennisScore === 0 => blocked (reason: off_topic).
 * - Else allowed.
 */
export function evaluateScope(text: string): ScopeResult {
  if (!text || typeof text !== "string") {
    return { allowed: true };
  }
  const trimmed = text.trim();
  if (!trimmed) return { allowed: true };

  const tennisScore = getTennisScore(trimmed);
  if (tennisScore >= 1) return { allowed: true };

  const offTopicScore = getOffTopicScore(trimmed);
  if (offTopicScore >= 1 && tennisScore === 0) return { allowed: false, reason: "off_topic" };

  return { allowed: true };
}
